import { MEDIA_ACCEPTED_TYPES, MEDIA_MAX_BYTES, mediaVariant } from '@growup/site-shared';
import { Button, ImageCropper, cx } from '@growup/ui';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';

import { messageOf, uploadImage } from '../api';

/**
 * Upload de imagens do back-office (docs/imagens): arrastar ou escolher a
 * foto (no telemovel abre a camara ou a galeria), enquadra-la no formato em
 * que o site a mostra (arrastar e aproximar) e so depois envia-la.
 *
 * - `ImageField`: uma imagem (eventos, passatempos, noticias, carrossel).
 * - `ImageGallery`: varias, por ordem, a primeira e a capa (produtos).
 *
 * O valor e o URL guardado (`/api/media/<id>/md.webp`), ou um URL antigo
 * (`https://...`) que continua a aparecer ate ser trocado. Enquanto ha uma
 * foto por enquadrar ou a enviar, `onBusyChange(true)`: o formulario nao deve
 * deixar guardar, senao a foto perde-se.
 */

const ACCEPT = MEDIA_ACCEPTED_TYPES.join(',');

/** Formatos (largura / altura) em que o site mostra cada imagem. */
export const IMAGE_ASPECTS = {
  product: 1,
  event: 16 / 9,
  giveaway: 16 / 9,
  tournament: 16 / 9,
  newsCover: 16 / 9,
  carousel: 1920 / 840,
  carouselMobile: 4 / 5,
  memorial: 4 / 5,
  award: 1,
} as const;

/** Lado maior do recorte enviado; o servidor faz daqui os tamanhos sm/md/lg. */
const CROP_SIZE = 1920;
/** Lado maior da moldura de recorte no ecra. */
const CROP_FRAME = 360;

/** O que o browser consegue dizer antes de enviar. A API volta a verificar tudo. */
export function checkImageFile(file: File): string | null {
  if (!(MEDIA_ACCEPTED_TYPES as readonly string[]).includes(file.type)) {
    return `«${file.name}» não é JPEG, PNG ou WebP.`;
  }
  if (file.size > MEDIA_MAX_BYTES) return `«${file.name}» tem mais de 10 MB.`;
  return null;
}

/** So as fotos guardadas aqui se podem voltar a enquadrar (as antigas vem de fora). */
const canAdjust = (url: string) => url.startsWith('/api/media/');

/** O recorte vai com o nome da foto original, para a reconhecer depois. */
function croppedFile(image: Blob, name: string): File {
  const ext = image.type === 'image/png' ? 'png' : 'webp';
  return new File([image], `${name.replace(/\.[^.]*$/, '') || 'foto'}.${ext}`, {
    type: image.type || 'image/webp',
  });
}

/** Separa os ficheiros validos dos outros; devolve o primeiro problema. */
function validFiles(files: File[]): { valid: File[]; problem: string | null } {
  let problem: string | null = null;
  const valid: File[] = [];
  for (const file of files) {
    const issue = checkImageFile(file);
    if (issue) problem ??= issue;
    else valid.push(file);
  }
  return { valid, problem };
}

/** Avisa o formulario quando comeca ou acaba o trabalho com fotos. */
function useBusyReport(busy: boolean, onBusyChange: ((busy: boolean) => void) | undefined) {
  const report = useRef(onBusyChange);
  report.current = onBusyChange;
  useEffect(() => {
    report.current?.(busy);
  }, [busy]);
  useEffect(() => () => report.current?.(false), []);
}

/**
 * Para o formulario: `busy` enquanto algum campo de imagem tem uma foto por
 * enquadrar ou a enviar. Cada campo usa `onBusyChange={track('nome')}`.
 */
export function useImagesBusy(): { busy: boolean; track: (id: string) => (busy: boolean) => void } {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());
  const track = useCallback(
    (id: string) => (busy: boolean) =>
      setIds((prev) => {
        if (prev.has(id) === busy) return prev;
        const next = new Set(prev);
        if (busy) next.add(id);
        else next.delete(id);
        return next;
      }),
    [],
  );
  return { busy: ids.size > 0, track };
}

/** Pre-visualizacao local enquanto a foto sobe para o servidor. */
type Upload = { key: number; preview: string };

function UploadingPreview({ preview, className }: { preview: string; className: string }) {
  return (
    <div className="relative">
      <img src={preview} alt="Foto a carregar" className={cx(className, 'opacity-60')} />
      <span
        role="status"
        className="absolute inset-0 flex items-center justify-center font-sub text-label font-bold uppercase text-gu-ink"
      >
        <span className="bg-gu-raised px-2 py-1">A carregar…</span>
      </span>
    </div>
  );
}

function DropZone({
  multiple,
  busy,
  disabled,
  onFiles,
  children,
  label,
}: {
  multiple: boolean;
  busy: boolean;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  children?: ReactNode;
  label: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const inert = busy || disabled === true;

  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (inert) return;
    const files = [...e.dataTransfer.files];
    if (files.length > 0) onFiles(multiple ? files : files.slice(0, 1));
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!inert) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
      className={cx(
        'flex flex-col items-center justify-center gap-2 border-2 border-dashed px-4 py-5 text-center',
        over ? 'border-gu-accent bg-gu-accent/10' : 'border-gu-line',
      )}
    >
      {children}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={inert}
        onClick={() => input.current?.click()}
      >
        {busy ? 'A carregar…' : label}
      </Button>
      <span className="text-caption text-gu-ink-muted">
        Ou arraste para aqui. JPEG, PNG ou WebP, até 10 MB.
      </span>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        multiple={multiple}
        hidden
        data-testid="image-input"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = '';
          if (files.length > 0) onFiles(files);
        }}
      />
    </div>
  );
}

function Label({
  id,
  children,
  optional,
}: {
  id: string;
  children: ReactNode;
  optional?: boolean | undefined;
}) {
  return (
    <span id={id} className="font-sub text-label font-bold uppercase text-gu-ink-muted">
      {children}
      {optional ? <span className="ml-1 normal-case tracking-normal">(opcional)</span> : null}
    </span>
  );
}

function Problem({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <span role="alert" className="text-caption font-semibold text-gu-danger">
      <span aria-hidden="true">✕ </span>
      {message}
    </span>
  );
}

/** Uma imagem. `onChange(null)` = sem imagem. */
export function ImageField({
  label,
  value,
  onChange,
  aspect,
  onBusyChange,
  error,
  optional,
  hint,
}: {
  label: string;
  value: string | null;
  onChange: (url: string | null) => void;
  /** Formato do recorte (largura / altura); sem ele a foto vai inteira. */
  aspect?: number;
  onBusyChange?: (busy: boolean) => void;
  error?: string | undefined;
  optional?: boolean;
  hint?: ReactNode;
}) {
  const labelId = useId();
  const [crop, setCrop] = useState<{ source: File | string; name: string } | null>(null);
  const [upload, setUpload] = useState<Upload | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  useBusyReport(crop !== null || upload !== null, onBusyChange);

  const send = async (file: File) => {
    const preview = URL.createObjectURL(file);
    setUpload({ key: 0, preview });
    setProblem(null);
    try {
      onChange((await uploadImage(file)).url);
    } catch (e) {
      setProblem(`«${file.name}»: ${messageOf(e)}`);
    } finally {
      URL.revokeObjectURL(preview);
      setUpload(null);
    }
  };

  const choose = (files: File[]) => {
    const { valid, problem: issue } = validFiles(files.slice(0, 1));
    setProblem(issue);
    const file = valid[0];
    if (!file) return;
    if (aspect === undefined) void send(file);
    else setCrop({ source: file, name: file.name });
  };

  return (
    <div role="group" aria-labelledby={labelId} className="flex flex-col gap-1.5">
      <Label id={labelId} optional={optional}>
        {label}
      </Label>
      {crop && aspect !== undefined ? (
        <ImageCropper
          source={crop.source}
          aspect={aspect}
          size={CROP_SIZE}
          frame={CROP_FRAME}
          saveLabel="Usar este recorte"
          onCrop={(image) => {
            setCrop(null);
            void send(croppedFile(image, crop.name));
          }}
          onUseOriginal={
            crop.source instanceof File
              ? () => {
                  const original = crop.source as File;
                  setCrop(null);
                  void send(original);
                }
              : undefined
          }
          onCancel={() => setCrop(null)}
        />
      ) : (
        <DropZone
          multiple={false}
          busy={upload !== null}
          onFiles={choose}
          label={value ? 'Trocar foto' : 'Escolher foto'}
        >
          {upload ? (
            <UploadingPreview
              preview={upload.preview}
              className="max-h-40 w-auto bg-gu-raised object-contain"
            />
          ) : value ? (
            <img
              src={mediaVariant(value, 'sm')}
              alt="Imagem atual"
              className="max-h-40 w-auto bg-gu-raised object-contain"
            />
          ) : null}
        </DropZone>
      )}
      {value && !crop && !upload ? (
        <div className="flex flex-wrap gap-2">
          {aspect !== undefined && canAdjust(value) ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setCrop({ source: mediaVariant(value, 'lg'), name: 'foto' })}
            >
              Ajustar
            </Button>
          ) : null}
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
            Remover foto
          </Button>
        </div>
      ) : null}
      {hint ? <span className="text-caption text-gu-ink-muted">{hint}</span> : null}
      <Problem message={problem ?? error} />
    </div>
  );
}

/** Varias imagens por ordem; a primeira e a capa. */
export function ImageGallery({
  label,
  value,
  onChange,
  max,
  aspect,
  onBusyChange,
  error,
}: {
  label: string;
  value: string[];
  onChange: (urls: string[]) => void;
  max: number;
  /** Formato do recorte (largura / altura); sem ele as fotos vao inteiras. */
  aspect?: number;
  onBusyChange?: (busy: boolean) => void;
  error?: string | undefined;
}) {
  const labelId = useId();
  /** Fotos escolhidas a espera de serem enquadradas, uma de cada vez. */
  const [queue, setQueue] = useState<File[]>([]);
  /** Foto ja guardada que se esta a voltar a enquadrar. */
  const [adjusting, setAdjusting] = useState<string | null>(null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  useBusyReport(queue.length > 0 || adjusting !== null || uploads.length > 0, onBusyChange);

  // Os envios vao um a um, pela ordem em que foram enquadrados; cada um junta-se
  // ao valor mais recente (o pai pode ainda nao ter voltado a desenhar).
  const latest = useRef(value);
  latest.current = value;
  const chain = useRef<Promise<void>>(Promise.resolve());
  const nextKey = useRef(0);

  const enqueue = (file: File, replaces?: string) => {
    const key = nextKey.current++;
    const preview = URL.createObjectURL(file);
    setUploads((list) => [...list, { key, preview }]);
    chain.current = chain.current.then(async () => {
      try {
        const { url } = await uploadImage(file);
        const next = replaces
          ? latest.current.map((u) => (u === replaces ? url : u))
          : [...latest.current, url];
        latest.current = next;
        onChange(next);
      } catch (e) {
        setProblem(`«${file.name}»: ${messageOf(e)}`);
      } finally {
        URL.revokeObjectURL(preview);
        setUploads((list) => list.filter((u) => u.key !== key));
      }
    });
  };

  const free = max - value.length - queue.length - uploads.length;
  const full = free <= 0;

  const add = (files: File[]) => {
    const { valid, problem: issue } = validFiles(files);
    let message = issue;
    if (valid.length > free) {
      message ??= `Só cabem mais ${String(Math.max(free, 0))}; as restantes ficaram de fora.`;
    }
    setProblem(message);
    const accepted = valid.slice(0, Math.max(free, 0));
    if (aspect === undefined) accepted.forEach((file) => enqueue(file));
    else setQueue((q) => [...q, ...accepted]);
  };

  const move = (from: number, to: number) => {
    const next = [...value];
    const [item] = next.splice(from, 1);
    if (item === undefined) return;
    next.splice(to, 0, item);
    onChange(next);
  };

  const current = queue[0];
  const cropping =
    aspect === undefined
      ? null
      : adjusting !== null
        ? { source: mediaVariant(adjusting, 'lg') as File | string, name: 'foto' }
        : current
          ? { source: current as File | string, name: current.name }
          : null;

  return (
    <div role="group" aria-labelledby={labelId} className="flex flex-col gap-1.5">
      <Label id={labelId} optional>
        {label}
      </Label>
      {value.length > 0 || uploads.length > 0 ? (
        <ol className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {value.map((url, i) => (
            <li key={url} className="flex flex-col gap-1 border-2 border-gu-line p-1">
              <div className="relative">
                <img
                  src={mediaVariant(url, 'sm')}
                  alt={`Foto ${String(i + 1)}`}
                  className="aspect-square w-full bg-gu-raised object-cover"
                />
                {i === 0 ? (
                  <span className="absolute left-1 top-1 bg-gu-accent px-1.5 py-0.5 font-sub text-[0.625rem] font-bold uppercase text-gu-on-accent">
                    Capa
                  </span>
                ) : null}
              </div>
              <div className="flex items-center justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={i === 0}
                  aria-label={`Passar a foto ${String(i + 1)} para trás`}
                  onClick={() => move(i, i - 1)}
                >
                  ←
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Tirar a foto ${String(i + 1)}`}
                  onClick={() => onChange(value.filter((_, j) => j !== i))}
                >
                  ✕
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={i === value.length - 1}
                  aria-label={`Passar a foto ${String(i + 1)} para a frente`}
                  onClick={() => move(i, i + 1)}
                >
                  →
                </Button>
              </div>
              {aspect !== undefined && canAdjust(url) ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={cropping !== null}
                  aria-label={`Ajustar a foto ${String(i + 1)}`}
                  onClick={() => setAdjusting(url)}
                >
                  Ajustar
                </Button>
              ) : null}
            </li>
          ))}
          {uploads.map((u) => (
            <li key={`upload-${String(u.key)}`} className="border-2 border-gu-line p-1">
              <UploadingPreview
                preview={u.preview}
                className="aspect-square w-full bg-gu-raised object-cover"
              />
            </li>
          ))}
        </ol>
      ) : null}
      {cropping && aspect !== undefined ? (
        <div className="flex flex-col gap-1">
          {adjusting === null && queue.length > 1 ? (
            <span className="text-caption text-gu-ink-muted">
              Foto 1 de {String(queue.length)} por enquadrar.
            </span>
          ) : null}
          <ImageCropper
            key={adjusting ?? `${current?.name ?? ''}-${String(queue.length)}`}
            source={cropping.source}
            aspect={aspect}
            size={CROP_SIZE}
            frame={CROP_FRAME}
            saveLabel="Usar este recorte"
            onCrop={(image) => {
              const file = croppedFile(image, cropping.name);
              if (adjusting !== null) {
                enqueue(file, adjusting);
                setAdjusting(null);
              } else {
                enqueue(file);
                setQueue((q) => q.slice(1));
              }
            }}
            onUseOriginal={
              adjusting === null && current
                ? () => {
                    enqueue(current);
                    setQueue((q) => q.slice(1));
                  }
                : undefined
            }
            onCancel={() => {
              if (adjusting !== null) setAdjusting(null);
              else setQueue((q) => q.slice(1));
            }}
          />
        </div>
      ) : (
        <DropZone
          multiple
          busy={false}
          disabled={full}
          onFiles={add}
          label={full ? `Máximo de ${String(max)} fotos` : 'Adicionar fotos'}
        />
      )}
      <span className="text-caption text-gu-ink-muted">
        A primeira é a capa, a que aparece na loja e no carrinho. Use as setas para mudar a ordem.
      </span>
      <Problem message={problem ?? error} />
    </div>
  );
}
